import React, { memo, useCallback } from "react";
import { Container } from "@pixi/react";
import { Settings } from "../../settings";
import { useDimensions } from "../../Contexts/DimensionsContext";
import { useReelRefs } from "../../Contexts/ReelRefsContext";
import HoldAndWinTensionOverlay from "./HoldAndWinTensionOverlay";

/**
 * Hold and Win Tension Overlay Set
 * Creates a grid of tension overlays (5 reels × 4 symbols = 20 total)
 * Each overlay sits on top of its corresponding symbol position
 * Manages ONLY tension animations - completely independent from multiplier animations
 */
const HoldAndWinTensionOverlaySet = memo(() => {
  const { symbolWidth, symbolHeight } = useDimensions();
  const { holdAndWinTensionOverlayRefs } = useReelRefs();

  // Callback ref to register tension overlay with context
  const setTensionOverlayRef = useCallback(
    (reelIndex, symbolIndex) => (el) => {
      if (holdAndWinTensionOverlayRefs.current) {
        if (!holdAndWinTensionOverlayRefs.current[reelIndex]) {
          holdAndWinTensionOverlayRefs.current[reelIndex] = [];
        }
        holdAndWinTensionOverlayRefs.current[reelIndex][symbolIndex] = el;
      }
    },
    [holdAndWinTensionOverlayRefs]
  );

  // Create grid of tension overlays: 5 reels × 4 symbols
  // Positioning MUST match HoldAndWinOverlayReel exactly
  const tensionOverlays = [];
  for (let reelIndex = 0; reelIndex < Settings.REEL_COUNT; reelIndex++) {
    for (
      let symbolIndex = 0;
      symbolIndex < Settings.SYMBOLS_VISIBLE;
      symbolIndex++
    ) {
      // Match HoldAndWinOverlayReel positioning exactly:
      // - X: symbolWidth * (index - 2) with NO reelSpacing
      // - Y: symbolHeight * idx + idx (has +idx offset)
      const xPosition = symbolWidth * (reelIndex - 2);
      const yPosition = symbolHeight * symbolIndex + symbolIndex;

      tensionOverlays.push(
        <Container
          key={`${reelIndex}-${symbolIndex}`}
          name={`tension_overlay_container_${reelIndex}_${symbolIndex}`}
          x={xPosition}
          y={yPosition}
        >
          <HoldAndWinTensionOverlay
            ref={setTensionOverlayRef(reelIndex, symbolIndex)}
            reelIndex={reelIndex}
            symbolIndex={symbolIndex}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
          />
        </Container>
      );
    }
  }

  return (
    <Container
      name="hold_and_win_tension_overlay_set"
      zIndex={8}
      x={0}
      y={-symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5)}
    >
      {tensionOverlays}
    </Container>
  );
});

export default HoldAndWinTensionOverlaySet;
